import { memo } from './memo';
import { useMemo } from 'preact/hooks';
import {
  HEX_DIRECTIONS,
  hexCorners,
  hexKey,
  hexToPixel,
  topologyOf,
  type Board as BoardData,
  type Building,
  type Hex,
  type Port,
  type Topology,
} from '../engine';
import { useSeatColor } from './colors';
import { RESOURCE_COLORS, RESOURCE_LABELS, RESOURCE_TEXT, TERRAIN_COLORS } from './theme';
import type { Terrain } from '../engine';

const S = 60;

export interface BoardTargets {
  vertices?: ReadonlySet<number>;
  edges?: ReadonlySet<number>;
  hexes?: ReadonlySet<number>;
}

export interface BoardProps {
  board: BoardData;
  robber: number;
  buildings?: readonly (Building | null)[];
  roads?: readonly (number | null)[];
  targets?: BoardTargets;
  onVertex?: (v: number) => void;
  onEdge?: (e: number) => void;
  onHex?: (h: number) => void;
  /** Briefly flash every hex with this number token; change `key` to replay. */
  flash?: { token: number; key: number } | null;
  /** Spotlight one player's pieces (the rest dim). */
  focus?: number | null;
  /** Hovering a piece reports its owner (null on leave), so the players list can light up too. */
  onFocusSeat?: (seat: number | null) => void;
}

const points = (pts: { x: number; y: number }[]) => pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

function pips(token: number): number {
  return 6 - Math.abs(7 - token);
}

function Token({ x, y, token }: { x: number; y: number; token: number }) {
  const red = token === 6 || token === 8;
  const n = pips(token);
  return (
    <g class="token">
      <circle cx={x} cy={y} r={S * 0.3} fill="#fdf6e3" stroke="#6b4f2a" stroke-width="1.5" />
      <text class="token-num" x={x} y={y + S * 0.06} text-anchor="middle" font-size={S * 0.28} font-weight="700" fill={red ? '#c1121f' : '#1b1b1b'}>
        {token}
      </text>
      {Array.from({ length: n }, (_, i) => (
        <circle cx={x + (i - (n - 1) / 2) * S * 0.07} cy={y + S * 0.17} r={S * 0.025} fill={red ? '#c1121f' : '#1b1b1b'} />
      ))}
    </g>
  );
}

interface Geometry {
  t: Topology;
  vx: (v: number) => number;
  vy: (v: number) => number;
  hex: (h: number) => { x: number; y: number };
}

function geometry(t: Topology): Geometry {
  return {
    t,
    vx: (v) => t.vertices[v]!.x * S,
    vy: (v) => t.vertices[v]!.y * S,
    hex: (h) => hexToPixel(t.hexes[h]!, S),
  };
}

/** Land plus one ring of sea around it: islands and lakes show up as gaps in the land. */
function seaHexes(land: readonly Hex[]): Hex[] {
  const seen = new Set(land.map(hexKey));
  const out = land.slice();
  for (const h of land) {
    for (const d of HEX_DIRECTIONS) {
      const n = { q: h.q + d.q, r: h.r + d.r };
      if (seen.has(hexKey(n))) continue;
      seen.add(hexKey(n));
      out.push(n);
    }
  }
  return out;
}

function PortMarker({ port, g }: { port: Port; g: Geometry }) {
  const { t, vx, vy } = g;
  const [a, b] = t.edges[port.edge]!.vertices;
  const mx = (vx(a) + vx(b)) / 2;
  const my = (vy(a) + vy(b)) / 2;
  // Offset along the edge's normal, away from its one land hex, so the marker
  // sits square to the edge with even legs (on lake shores too).
  const land = g.hex(t.edges[port.edge]!.hexes[0]!);
  const dx = vx(b) - vx(a);
  const dy = vy(b) - vy(a);
  const dlen = Math.hypot(dx, dy);
  let nx = -dy / dlen;
  let ny = dx / dlen;
  if (nx * (mx - land.x) + ny * (my - land.y) < 0) {
    nx = -nx;
    ny = -ny;
  }
  const px = mx + nx * S * 0.62;
  const py = my + ny * S * 0.62;
  const resource = port.type === 'any' ? null : port.type;
  return (
    <g class="port">
      <line x1={vx(a)} y1={vy(a)} x2={px} y2={py} stroke="#e9d8a6" stroke-width="4" />
      <line x1={vx(b)} y1={vy(b)} x2={px} y2={py} stroke="#e9d8a6" stroke-width="4" />
      <circle cx={px} cy={py} r={S * 0.27} fill={resource ? RESOURCE_COLORS[resource] : '#fdf6e3'} stroke="#3d2c1e" stroke-width="1.5" />
      <text class="port-rate" x={px} y={py + S * 0.03} text-anchor="middle" font-size={S * 0.18} font-weight="700" fill={resource ? RESOURCE_TEXT[resource] : '#1b1b1b'}>
        {resource ? '2:1' : '3:1'}
      </text>
      {resource && (
        <text class="port-label" x={px} y={py + S * 0.19} text-anchor="middle" font-size={S * 0.11} fill={RESOURCE_TEXT[resource]}>
          {RESOURCE_LABELS[resource]}
        </text>
      )}
    </g>
  );
}

function Settlement({ x, y, color }: { x: number; y: number; color: string }) {
  const s = S * 0.17;
  const house = [
    { x: x - s, y: y + s },
    { x: x - s, y: y - s * 0.3 },
    { x, y: y - s * 1.2 },
    { x: x + s, y: y - s * 0.3 },
    { x: x + s, y: y + s },
  ];
  return <Halo points={points(house)} color={color} />;
}

/** A light outline under each piece so it reads on any terrain and in any player colour. */
function Halo({ points: pts, color }: { points: string; color: string }) {
  return (
    <>
      <polygon points={pts} fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="6" stroke-linejoin="round" />
      <polygon points={pts} fill={color} stroke="#1b1b1b" stroke-width="2" stroke-linejoin="round" />
    </>
  );
}

/** Small terrain symbols (drawn, not images): they help when colours are hard to tell apart. */
function TerrainIcon({ terrain, x, y }: { terrain: Terrain; x: number; y: number }) {
  const s = S * 0.12;
  const light = terrain === 'forest' || terrain === 'hills';
  const ink = light ? 'rgba(255,255,255,0.7)' : 'rgba(27,27,27,0.45)';
  const t = `translate(${x.toFixed(1)} ${y.toFixed(1)})`;
  switch (terrain) {
    case 'forest':
      return (
        <g class="terrain-icon" transform={t} fill={ink}>
          <polygon points={`0,${-s * 1.2} ${s},${s * 0.4} ${-s},${s * 0.4}`} />
          <rect x={-s * 0.18} y={s * 0.4} width={s * 0.36} height={s * 0.6} />
        </g>
      );
    case 'pasture':
      return (
        <g class="terrain-icon" transform={t} fill={ink}>
          <ellipse cx={0} cy={0} rx={s * 0.9} ry={s * 0.6} />
          <circle cx={s * 1.05} cy={-s * 0.25} r={s * 0.35} />
        </g>
      );
    case 'fields':
      return (
        <g class="terrain-icon" transform={t} stroke={ink} stroke-width={s * 0.22} stroke-linecap="round" fill="none">
          {[-0.6, 0, 0.6].map((dx) => (
            <line key={dx} x1={dx * s} y1={s} x2={dx * s} y2={-s} />
          ))}
        </g>
      );
    case 'hills':
      return (
        <g class="terrain-icon" transform={t} fill={ink}>
          <rect x={-s} y={-s * 0.6} width={s * 0.9} height={s * 0.5} />
          <rect x={s * 0.1} y={-s * 0.6} width={s * 0.9} height={s * 0.5} />
          <rect x={-s * 0.45} y={s * 0.05} width={s * 0.9} height={s * 0.5} />
        </g>
      );
    case 'mountains':
      return (
        <g class="terrain-icon" transform={t} fill={ink}>
          <polygon points={`${-s * 1.2},${s * 0.6} ${-s * 0.3},${-s * 0.9} ${s * 0.6},${s * 0.6}`} />
          <polygon points={`${-s * 0.1},${s * 0.6} ${s * 0.6},${-s * 0.4} ${s * 1.3},${s * 0.6}`} />
        </g>
      );
    case 'desert':
      return <circle class="terrain-icon" cx={x} cy={y} r={s * 0.7} fill={ink} />;
  }
}

function City({ x, y, color }: { x: number; y: number; color: string }) {
  const s = S * 0.17;
  const shape = [
    { x: x - s * 1.5, y: y + s },
    { x: x - s * 1.5, y: y - s * 0.4 },
    { x: x - s * 0.7, y: y - s * 1.3 },
    { x: x + s * 0.1, y: y - s * 0.4 },
    { x: x + s * 0.1, y: y - s * 0.2 },
    { x: x + s * 1.5, y: y - s * 0.2 },
    { x: x + s * 1.5, y: y + s },
  ];
  return <Halo points={points(shape)} color={color} />;
}

// Positioned with a CSS transform (not cx/cy) so moving it can transition.
function Robber({ x, y }: { x: number; y: number }) {
  return (
    <g class="robber" data-testid="robber" style={{ transform: `translate(${x}px, ${y}px)` }}>
      <ellipse cx={0} cy={S * 0.16} rx={S * 0.15} ry={S * 0.2} fill="#2b2d42" stroke="#edf2f4" stroke-width="1.5" />
      <circle cx={0} cy={-S * 0.1} r={S * 0.1} fill="#2b2d42" stroke="#edf2f4" stroke-width="1.5" />
    </g>
  );
}

/** Board targets are SVG shapes, so give them button semantics and Enter/Space by hand. */
function targetProps(label: string, activate?: () => void) {
  return {
    role: 'button' as const,
    // Lowercase: on SVG elements preact sets attributes verbatim, and `tabIndex` isn't one.
    tabindex: 0,
    'aria-label': label,
    onClick: activate,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      activate?.();
    },
  };
}

function hexLabel(board: BoardData, h: number): string {
  const tile = board.tiles[h];
  if (!tile) return `hex ${h}`;
  return tile.token === null ? tile.terrain : `${tile.terrain} ${tile.token}`;
}

/**
 * Memoised: the game screen re-renders for clocks, toasts and panels, and the
 * board is the most expensive thing on the page. Callers keep props stable.
 */
export const Board = memo(function Board({ board, robber, buildings = [], roads = [], targets = {}, onVertex, onEdge, onHex, flash, focus = null, onFocusSeat }: BoardProps) {
  const seatColor = useSeatColor();
  const t = topologyOf({ board });
  const g = useMemo(() => geometry(t), [t]);
  const { vx, vy } = g;
  const sea = useMemo(() => {
    const cells = seaHexes(t.hexes).map((h) => hexCorners(hexToPixel(h, S), S + 1));
    const xs = cells.flat().map((p) => p.x);
    const ys = cells.flat().map((p) => p.y);
    const pad = S * 0.15;
    const box = { x: Math.min(...xs) - pad, y: Math.min(...ys) - pad, w: 0, h: 0 };
    box.w = Math.max(...xs) + pad - box.x;
    box.h = Math.max(...ys) + pad - box.y;
    return { cells, box };
  }, [t]);
  const { box } = sea;
  return (
    <svg
      class={`board${focus !== null ? ' focusing' : ''}`}
      viewBox={`${box.x.toFixed(1)} ${box.y.toFixed(1)} ${box.w.toFixed(1)} ${box.h.toFixed(1)}`}
      role="group"
      aria-label="Game board"
    >
      <g class="sea">
        {sea.cells.map((c, i) => (
          <polygon key={i} points={points(c)} fill="var(--sea)" />
        ))}
      </g>
      {board.ports.map((p) => (
        <PortMarker key={`port-${p.edge}`} port={p} g={g} />
      ))}
      {board.tiles.map((tile, i) => {
        const c = g.hex(i);
        return (
          <g key={`hex-${i}`} class={`tile ${tile.terrain}`} data-hex={i}>
            <polygon points={points(hexCorners(c, S - 1.5))} fill={TERRAIN_COLORS[tile.terrain]} stroke="#f1faee" stroke-width="3" />
            <TerrainIcon terrain={tile.terrain} x={c.x} y={c.y - S * 0.58} />
            {tile.token !== null && <Token x={c.x} y={c.y} token={tile.token} />}
          </g>
        );
      })}
      {flash &&
        board.tiles.map((tile, i) =>
          tile.token === flash.token && i !== robber ? (
            <polygon
              key={`flash-${flash.key}-${i}`}
              class="flash"
              points={points(hexCorners(g.hex(i), S - 4))}
            />
          ) : null,
        )}
      {roads.map((owner, e) => {
        if (owner === null) return null;
        const [a, b] = t.edges[e]!.vertices;
        const inset = 0.16;
        const x1 = vx(a) + (vx(b) - vx(a)) * inset;
        const y1 = vy(a) + (vy(b) - vy(a)) * inset;
        const x2 = vx(b) + (vx(a) - vx(b)) * inset;
        const y2 = vy(b) + (vy(a) - vy(b)) * inset;
        return (
          <g
            key={`road-${e}`}
            class={`road${owner === focus ? ' focused' : ''}`}
            data-edge={e}
            data-owner={owner}
            onPointerEnter={() => onFocusSeat?.(owner)}
            onPointerLeave={() => onFocusSeat?.(null)}
          >
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.85)" stroke-width={S * 0.23} stroke-linecap="round" />
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1b1b1b" stroke-width={S * 0.17} stroke-linecap="round" />
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={seatColor(owner)} stroke-width={S * 0.11} stroke-linecap="round" />
          </g>
        );
      })}
      {(() => {
        if (robber < 0 || robber >= t.hexes.length) return null;
        const c = g.hex(robber);
        return <Robber x={c.x - S * 0.42} y={c.y} />;
      })()}
      {buildings.map((b, v) => {
        if (!b) return null;
        const Piece = b.kind === 'city' ? City : Settlement;
        return (
          <g
            key={`b-${v}`}
            class={`building ${b.kind}${b.owner === focus ? ' focused' : ''}`}
            data-vertex={v}
            data-owner={b.owner}
            onPointerEnter={() => onFocusSeat?.(b.owner)}
            onPointerLeave={() => onFocusSeat?.(null)}
          >
            <Piece x={vx(v)} y={vy(v)} color={seatColor(b.owner)} />
          </g>
        );
      })}

      {[...(targets.hexes ?? [])].map((h) => {
        const c = g.hex(h);
        return (
          <polygon
            key={`th-${h}`}
            class="target target-hex"
            data-testid={`hex-${h}`}
            points={points(hexCorners(c, S * 0.88))}
            {...targetProps(`Move the robber to ${hexLabel(board, h)}`, () => onHex?.(h))}
          />
        );
      })}
      {[...(targets.edges ?? [])].map((e) => {
        const [a, b] = t.edges[e]!.vertices;
        const mx = (vx(a) + vx(b)) / 2;
        const my = (vy(a) + vy(b)) / 2;
        const angle = (Math.atan2(vy(b) - vy(a), vx(b) - vx(a)) * 180) / Math.PI;
        const len = S * 0.62;
        const w = S * 0.24;
        return (
          <rect
            key={`te-${e}`}
            class="target target-edge"
            data-testid={`edge-${e}`}
            x={mx - len / 2}
            y={my - w / 2}
            width={len}
            height={w}
            rx={w / 2}
            transform={`rotate(${angle.toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)})`}
            {...targetProps(`Road by ${t.edges[e]!.hexes.map((h) => hexLabel(board, h)).join(', ') || 'the coast'}`, () => onEdge?.(e))}
          />
        );
      })}
      {[...(targets.vertices ?? [])].map((v) => (
        <circle
          key={`tv-${v}`}
          class="target target-vertex"
          data-testid={`vertex-${v}`}
          cx={vx(v)}
          cy={vy(v)}
          r={S * 0.2}
          {...targetProps(`Corner by ${t.vertices[v]!.hexes.map((h) => hexLabel(board, h)).join(', ')}`, () => onVertex?.(v))}
        />
      ))}
    </svg>
  );
});
